import { useMutation, useQueryClient } from '@tanstack/react-query';

import { notificationQueries, readAllNotifications } from '../api/notification';
import useNotificationApiInstance from './useNotificationApiInstance';

import type { AxiosError } from 'axios';
import type { InfiniteData, UseMutationOptions } from '@tanstack/react-query';
import type { GetNotificationFeedResponse } from '../constants';

type NotificationFeedCache = InfiniteData<GetNotificationFeedResponse, string | undefined>;

type UseReadAllNotificationsOptions = UseMutationOptions<void, AxiosError, void>;

/**
 * 전체 Notification 읽음 처리 ("모두 읽음" 버튼).
 * @description
 * - 낙관적 업데이트: feed 캐시의 모든 알림을 즉시 read=true 로 선반영한다.
 * - 성공하면 `notificationQueries.all()` 을 invalidate 해서 feed/badge 를 최신화.
 */
const useReadAllNotifications = (options?: UseReadAllNotificationsOptions) => {
  const queryClient = useQueryClient();
  const { instance } = useNotificationApiInstance();

  return useMutation({
    mutationFn: () => readAllNotifications(instance),
    ...options,
    onMutate: async variables => {
      const queryKey = [...notificationQueries.all(), 'feed'] as const;

      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueriesData<NotificationFeedCache>({ queryKey }, old => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            data: page.data.map(notification => ({ ...notification, read: true })),
          })),
        };
      });

      await options?.onMutate?.(variables);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: notificationQueries.all() });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export default useReadAllNotifications;
